import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Mail, User, Building2, ArrowLeft, X, Phone, Gift, Tally4 } from 'lucide-react'; 
import { trackMetaEvent } from '../../utils/metaPixel'; 

// *** Reusable Terms of Service Modal Component ***
const TermsModal = ({ isOpen, onClose, onConfirmSignup, content, isLoading }) => {
    const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
    const contentRef = useRef(null);

    const handleScroll = () => {
        const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
        if (scrollHeight - scrollTop <= clientHeight + 5) {
            setIsScrolledToBottom(true);
        }
    };
    
    useEffect(() => {
        if (isOpen) {
            setIsScrolledToBottom(false);
            if(contentRef.current) contentRef.current.scrollTop = 0;
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-2xl shadow-2xl border border-white/20 w-full max-w-2xl flex flex-col max-h-[90vh]">
                <div className="flex justify-between items-center p-6 border-b border-gray-700">
                    <h2 className="text-2xl font-bold text-white">Terms of Service</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
                        <X size={28} />
                    </button>
                </div>
                <div 
                    ref={contentRef}
                    onScroll={handleScroll}
                    className="p-6 text-gray-300 overflow-y-auto custom-scrollbar flex-grow"
                >
                    {content.split('\n').map((paragraph, index) => (
                        <p key={index} className="mb-4 text-sm">{paragraph}</p>
                    ))}
                </div>
                <div className="p-6 border-t border-gray-700">
                    <button
                        onClick={onConfirmSignup}
                        disabled={!isScrolledToBottom || isLoading}
                        className="w-full py-3 px-4 bg-sky-600 text-white font-bold rounded-lg transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-sky-700"
                    >
                        {isLoading ? 'Preparing...' : (isScrolledToBottom ? 'I Agree & Choose Plan' : 'Scroll to the bottom to agree')}
                    </button>
                </div>
            </div>
        </div>
    );
};

const SignUpPage = () => {
    const [companyName, setCompanyName] = useState('');
    const [contactPersonName, setContactPersonName] = useState('');
    const [contactPhone, setContactPhone] = useState('');
    const [email, setEmail] = useState('');
    // ⭐ PASSWORD STATE REMOVED: Shifted to PricingPage for security
    const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { executeRecaptcha } = useGoogleReCaptcha();

    // Capture URL Parameters
    const referralCodeFromUrl = searchParams.get('ref');
    const referralTypeFromUrl = searchParams.get('type'); 
    const franchisorCodeFromUrl = searchParams.get('fcode');
    const offerCodeFromUrl = searchParams.get('offer');
    const isXmasOffer = offerCodeFromUrl === 'xmas2025';

    // Logic Checks
    const hasActiveDiscount = !!referralCodeFromUrl && referralTypeFromUrl !== 'full';
    const isFranchiseeSignup = !!franchisorCodeFromUrl;

    const termsContent = `Last updated: 28 September, 2025
Introduction Welcome to The One by ProCleanerUK. By accessing and using our website, services, and mobile applications (collectively, the "Service"), you agree to be bound by these Terms of Service. These Terms apply to all visitors and users. If you do not agree with any part of these Terms, you must not use our Service.
Changes to Terms We reserve the right to update or change these Terms at our discretion. It is your responsibility to check this page periodically for changes. Your continued use of the Service after changes are posted constitutes acceptance of those changes.
1. Account & Trial Terms
Eligibility: You must be at least 18 years old to use this Service.
Account Registration: You must provide accurate and complete information during signup. You are responsible for all activity that occurs under your account.
${isFranchiseeSignup ? 
    'Franchisee Account: By signing up with a Franchisor Code, your account will be linked to your franchisor’s master account. This does not waive the 28-day free trial unless explicitly stated by your franchisor. Your data remains private and separate from other franchisees.' : 
    'Free Trial: Your free trial lasts for twenty-eight (28) days, starting from the moment you sign up. During the trial, you will have access to the full features of the platform.'
}
2. Payments, Refunds, and Subscription Terms
Card Verification & Automatic Billing: To activate your 28-day free trial, a valid credit or debit card is required for verification purposes to prevent fraud and system abuse. You will not be charged during the initial 28-day trial period. However, please be aware that your paid subscription will begin automatically on Day 29, and your card will be charged for your selected plan unless you cancel your account before the trial expires.

Billing Cycle: If you do not cancel, you will be billed on Day 29 and every 30 days thereafter for monthly plans, or annually for yearly plans.

Non-Refundable Policy: Once a payment is processed on Day 29 or any subsequent renewal date, all payments are final and non-refundable. We do not provide refunds or credits for partial subscription periods or if you choose to cancel after a billing cycle has started.

3. Cancellation and Data Deletion
User Responsibility for Cancellation: You are solely responsible for cancelling your account before the end of the 28-day trial if you do not wish to be charged. We are under no obligation to notify, warn, or remind you before your trial ends or before any subsequent charge is taken, and the absence of such a notification does not entitle you to a refund.

How to Cancel: You can cancel and delete your account at any time by navigating to the Settings tab inside your dashboard, then selecting Account Settings. Clicking the Delete button will initiate the removal process.

Proof of Cancellation: You are responsible for confirming that your cancellation has been completed. In the event of a billing dispute, a cancellation will only be recognised as valid if you can provide one of the following: (a) confirmation via the Delete/Cancel button inside your Account Settings tab, (b) a dated email to or from us confirming cancellation, (c) a dated text message, (d) a recording of a phone call in which cancellation was requested and confirmed, (e) a webchat transcript confirming cancellation, or (f) any other communication made to us in writing confirming cancellation. Believing you had cancelled, or an intention to cancel that was not carried out or confirmed through one of these channels, does not constitute a valid cancellation and will not entitle you to a refund.

Data Deletion & Card Removal: Upon selecting the delete option, we perform an "Atomic Delete" of all data associated with your account. This process is permanent and irreversible. Additionally, your card details and payment methods will be immediately and permanently removed from our merchant system (Stripe).
4. Modifications to the Service and Prices
Service Changes: We reserve the right to modify or discontinue any part of the Service at any time, with or without notice.
Price Changes: Prices for all Services are subject to change upon 30 days’ notice from us.
5. Copyright and Content Ownership
User Content: We claim no intellectual property rights over the material you provide. Your data remains yours.
ProCleanerUK Content: The Service and its original content, features, and functionality are and will remain the exclusive property of ProCleanerUK Limited.
6. General Conditions
Service Provision: The Service is provided "as is" and "as available".
Support: Technical support is available to active account holders.
Prohibited Actions: You agree not to reproduce, duplicate, copy, sell, or exploit any portion of the Service without our express written permission.
7. Limitation of Liability
To the maximum extent permitted by law, ProCleanerUK shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits or data, resulting from your use of the Service.
8. Governing Law
These Terms shall be governed by the laws of the United Kingdom.
9. Contact Us
For any questions about these Terms, please contact us at info@procleaneruk.co.uk.`;

    const handleOpenTerms = (e) => {
        e.preventDefault();
        
        // ⭐ PASSWORD REMOVED from validation here
        if (!companyName || !contactPersonName || !contactPhone || !email) {
            setError("Please fill out all fields before proceeding.");
            return;
        }
        
        const strippedPhone = contactPhone.replace(/\s/g, ''); 
        const phoneRegex = /^\+?[0-9]{9,15}$/; 

        if (!phoneRegex.test(strippedPhone)) {
            setError("Please enter a valid phone number. It must be 9-15 digits, (e.g., +447123456789 or 07123456789).");
            return;
        }

        setError(null);
        setIsTermsModalOpen(true);
    };

    /**
     * ⭐ LAZY REGISTRATION LOGIC
     * Instead of creating the account now, we bundle the data 
     * and pass it to the Pricing Page.
     */
    const handleConfirmSignup = useCallback(async () => {
        setLoading(true);
        try {
            // We bundle the data into a single object
            const pendingUserData = {
                email,
                // ⭐ PASSWORD REMOVED: It is no longer stored in sessionStorage!
                companyName,
                contactPersonName,
                contactPhone,
                referralCode: referralCodeFromUrl,
                referralOfferType: referralTypeFromUrl === 'full' ? 'full' : 'split',
                franchisorCode: franchisorCodeFromUrl,
                offerCode: offerCodeFromUrl
            };

            // Meta pixel: they filled in their details and agreed to the terms
            try { trackMetaEvent('Lead', { content_name: 'Sign Up Form' }); } catch (e) { /* never block signup */ }

            // ⭐ THE FIX: Save to sessionStorage so data survives if iOS Safari suspends the tab
            sessionStorage.setItem('pendingUserData', JSON.stringify(pendingUserData));

            // Navigate to pricing and pass the data in location state
            // Re-CAPTCHA token will be generated FRESH on the pricing page to avoid timeouts
            navigate('/pricing', { 
                replace: true, 
                state: { 
                    reason: 'activate_trial',
                    pendingUserData 
                } 
            });
        } catch (err) {
            setError("Preparation failed. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [
        email, 
        companyName, 
        contactPersonName, 
        contactPhone, 
        navigate, 
        referralCodeFromUrl, 
        referralTypeFromUrl,
        franchisorCodeFromUrl,
        offerCodeFromUrl
    ]);

    return (
        <>
            <TermsModal 
                isOpen={isTermsModalOpen}
                onClose={() => setIsTermsModalOpen(false)}
                onConfirmSignup={handleConfirmSignup}
                content={termsContent}
                isLoading={loading}
            />
            <div className="min-h-screen w-full flex items-center justify-center bg-gray-900 p-4 overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <div className="area" style={{ background: "linear-gradient(135deg,#0b1730 0%,#12306b 55%,#0e7490 100%)" }}>
                        <ul className="circles">
                            <li></li><li></li><li></li><li></li><li></li><li></li><li></li><li></li><li></li><li></li>
                        </ul>
                    </div>
                </div>
                <div className="relative z-10 bg-white/10 backdrop-blur-md p-8 sm:p-10 rounded-2xl shadow-2xl border border-white/20 w-full max-w-md">
                    <div className="flex justify-center mb-8">
                        <img src="frontpagelogo.png" alt="Company Logo" className="w-24 h-24 bg-transparent" />
                    </div>
                    <h2 className="text-3xl font-extrabold text-center text-white mb-2">Create Your Account</h2>
                    <p className="text-center text-gray-300 mb-2">Start your free trial today.</p>
                    <p className="text-center text-sm text-gray-400 mb-8">
                        {isFranchiseeSignup
                            ? '28 days free, then £49 a month. Cancel any time.'
                            : `28 days free, then from ${hasActiveDiscount ? '£71.99' : '£79.99'} a month. Cancel any time.`}
                    </p>

                    {isXmasOffer && (
                        <div className="bg-red-900/40 border border-red-500 text-red-100 px-4 py-3 rounded-lg mb-6 flex items-center shadow-lg shadow-red-900/20 animated-element">
                            <Gift className="h-6 w-6 mr-3 text-red-400 flex-shrink-0 animate-bounce" />
                            <div className="text-sm">
                                <span className="font-bold block text-red-300">🎄 Christmas Special Applied!</span>
                                You're signing up for the Xmas Starter Deal.
                            </div>
                        </div>
                    )}

                    {isFranchiseeSignup && (
                         <div className="bg-blue-500/20 border border-blue-500 text-blue-100 px-4 py-3 rounded-lg mb-6 flex items-center shadow-lg shadow-blue-900/20">
                            <Tally4 className="h-5 w-5 mr-3 text-blue-400 flex-shrink-0" />
                            <div className="text-sm">
                                <span className="font-bold block">Franchise Signup Active!</span>
                                Your company will be linked to your Franchisor.
                            </div>
                        </div>
                    )}
                    
                    {hasActiveDiscount && (
                        <div className="bg-green-500/20 border border-green-500 text-green-100 px-4 py-3 rounded-lg mb-6 flex items-center animate-pulse shadow-lg shadow-green-900/20">
                            <Gift className="h-5 w-5 mr-3 text-green-400 flex-shrink-0" />
                            <div className="text-sm">
                                <span className="font-bold block">Referral Offer Activated!</span>
                                You will receive 10% off your subscription.
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="bg-red-500/30 border border-red-500 text-white px-4 py-3 rounded-lg mb-6 text-sm" role="alert">
                            {error}
                        </div>
                    )}
                    
                    <form onSubmit={handleOpenTerms} className="space-y-6">
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Building2 className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                value={companyName}
                                onChange={(e) => setCompanyName(e.target.value)}
                                placeholder="Company Name"
                                required
                                disabled={loading}
                                className="appearance-none relative block w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-white/20 rounded-md shadow-sm placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-sky-500 sm:text-sm transition duration-150 ease-in-out"
                            />
                        </div>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <User className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                value={contactPersonName}
                                onChange={(e) => setContactPersonName(e.target.value)}
                                placeholder="Your Name"
                                required
                                disabled={loading}
                            className="appearance-none relative block w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-white/20 rounded-md shadow-sm placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-sky-500 sm:text-sm transition duration-150 ease-in-out"
                            />
                        </div>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Phone className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                type="tel"
                                value={contactPhone}
                                onChange={(e) => setContactPhone(e.target.value)}
                                placeholder="Phone Number"
                                required
                                disabled={loading}
                                className="appearance-none relative block w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-white/20 rounded-md shadow-sm placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-sky-500 sm:text-sm transition duration-150 ease-in-out"
                            />
                        </div>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Mail className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Email (for your admin account)"
                                required
                                disabled={loading}
                                className="appearance-none relative block w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-white/20 rounded-md shadow-sm placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-sky-500 sm:text-sm transition duration-150 ease-in-out"
                            />
                        </div>
                        
                        {/* ⭐ PASSWORD FIELD WAS HERE. It has been moved to PricingPage.js for security. */}

                        <p className="text-xs text-center text-gray-400 pt-2">
                           By clicking "Sign Up", you will be prompted to agree to our <a href="https://procleaneruk.co.uk/terms-of-use/" target="_blank" rel="noopener noreferrer" className="font-medium text-sky-400 hover:text-sky-300 underline">Terms of Use</a> and <a href="https://procleaneruk.co.uk/privacy-policy/" target="_blank" rel="noopener noreferrer" className="font-medium text-sky-400 hover:text-sky-300 underline">Privacy Policy</a>.
                        </p>

                        <button
                            type="submit"
                            className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-semibold rounded-md text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-sky-500 transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed"
                            disabled={loading}
                        >
                            {loading ? 'Processing...' : 'Sign Up'}
                        </button>
                    </form>
                    <div className="mt-8 text-center text-sm text-gray-400">
                        <Link to="/login" className="font-medium text-sky-400 hover:text-sky-300 transition duration-150 ease-in-out inline-flex items-center">
                        <ArrowLeft className="h-4 w-4 mr-2" /> Already have an account? Login
                        </Link>
                        <p className="mt-4 text-xs text-gray-500">
                            Want a look around first?{' '}
                            <a
                                href="https://procleaneruk.co.uk"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-400 hover:text-gray-300 underline"
                            >
                                See what ProCleanerUK can do
                            </a>
                        </p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SignUpPage;